import { useEffect, useRef, useState } from 'react';

import { ISelectedPlan, QR_CODE_PRODUCT_ID } from 'components';
import { IDBDowngradeRequest, IDBMuriquiProject, useGetMuriquiProject, useToast, useGetActivePlans, useGetTamarinProject } from 'hooks';
import isEmpty from 'lodash.isempty';
import { useRouter } from 'next/router';
import { IDBTamarinProject } from 'types';

import PlanContext from './PlanContext';
import { getPlanPeriod } from './PlanContext.helpers';
import { PlanContextProps } from './PlanContext.interfaces';

export function PlanProvider({ children }: PlanContextProps) {
  const { handleToastError } = useToast();
  const {
    query: { projectId: queryProjectId },
  } = useRouter();

  const [projectId, setProjectId] = useState<string | undefined>();
  const [planName, setPlanName] = useState<string | undefined>();
  const [isFreePlan, setIsFreePlan] = useState<boolean>(true);
  const [activeProject, setActiveProject] = useState<IDBTamarinProject>();
  const [activePlan, setActivePlan] = useState<ISelectedPlan>();
  const [currentRemainingScans, setCurrentRemainingScans] = useState<number>(100);
  const [planScanAmount, setPlanScanAmount] = useState<number>(100);
  const [daysRemaining, setDaysRemaining] = useState<number>();
  const [planPeriod, setPlanPeriod] = useState<string>('');
  const [downgradeRequest, setDowngradeRequest] = useState<IDBDowngradeRequest | undefined>();

  // The project the plan is being loaded for. Responses are applied only while it is still this project: apollo-providers
  // runs the latest onCompleted for every response, so a slow one for the previous project would otherwise overwrite this one
  const currentProjectIdRef = useRef<string | undefined>(undefined);

  const applyMuriquiProject = ({ customerDetail }: IDBMuriquiProject) => {
    // A project with no QUICKCODES customer record yet has no subscription data, so the free plan defaults are kept
    const { subscriptions, subscriptionDate } = customerDetail ?? {};

    if (subscriptionDate) {
      const { formattedPeriod, remainingDays } = getPlanPeriod(new Date(subscriptionDate));
      setDaysRemaining(remainingDays);
      setPlanPeriod(formattedPeriod);
    }

    if (!subscriptions || subscriptions.length === 0) return;
    const [subscription] = subscriptions;
    const { downgradeRequest: request } = subscription;
    if (!request) return;
    setDowngradeRequest(request);
  };

  const applyTamarinProject = (project: IDBTamarinProject) => {
    if (!isEmpty(project) && project.isActive) {
      setActiveProject(project);
      setCurrentRemainingScans(project.remainingScans);
    } else {
      setCurrentRemainingScans(100);
    }
  };

  const { handleGetMuriquiProject } = useGetMuriquiProject({
    fetchPolicy: 'no-cache',
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleGetTamarinProject } = useGetTamarinProject({
    fetchPolicy: 'no-cache',
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleGetActivePlans } = useGetActivePlans({
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  useEffect(() => {
    const { plan } = activePlan ?? {};
    if (!activePlan || !plan) {
      setIsFreePlan(true);
      setPlanScanAmount(100);
      setPlanName('Free');
    } else {
      const { name, value } = plan;
      setIsFreePlan(name.toLocaleLowerCase() === 'free');
      setPlanScanAmount(value);
      setPlanName(name);
    }
  }, [activePlan]);

  useEffect(() => {
    if (!projectId) return;

    // The provider stays mounted across dashboard navigation, so clear the previous project's plan before loading this one.
    // Clearing activeProject also makes the Tamarin project load again for the new project.
    setActivePlan(undefined);
    setActiveProject(undefined);
    setCurrentRemainingScans(100);
    setDaysRemaining(undefined);
    setDowngradeRequest(undefined);
    setPlanPeriod('');

    currentProjectIdRef.current = projectId;

    void handleGetMuriquiProject({
      projectId,
    }).then((project) => {
      if (project && currentProjectIdRef.current === projectId) {
        applyMuriquiProject(project);
      }
    });

    void handleGetActivePlans({
      projectId,
      product: 'QUICKCODES',
    }).then((activePlans) => {
      if (activePlans && currentProjectIdRef.current === projectId) {
        setActivePlan({
          plan: activePlans.find(({ productId }) => productId === QR_CODE_PRODUCT_ID),
        });
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (activeProject || !projectId) return;

    void handleGetTamarinProject({
      id: projectId,
    }).then((project) => {
      if (project && currentProjectIdRef.current === projectId) {
        applyTamarinProject(project);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, projectId]);

  useEffect(() => {
    setProjectId(queryProjectId as string);
  }, [queryProjectId]);

  return (
    <PlanContext.Provider
      value={{
        activePlan,
        downgradeRequest,
        isFreePlan,
        planName,
        planScanAmount,
        remainingScans: currentRemainingScans,
        daysRemaining,
        planPeriod,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}
